import { APP_POOL_NODE_GROUP_ID_PATTERN, appPoolRegionSchema, appPoolSpotCapacitySchema } from "@zion/sdk-contracts";
import { z } from "zod";
//#region src/live-node-pool.ts
/**
* R8-1 — the LIVE node-pool census wire item: how many nodes a data plane is **actually running**,
* per pool, as counted in the DP cluster.
*
* Produced by the DP operator (`node-census.ts`) from the `listNode()` call it already makes every
* reconcile; reported on `StatusReport.livePools`; persisted by release-engine into
* `release.dp_live_status.live_pools`; read cross-schema by provisioning-svc to fill
* `PoolComputeView.liveNodeCount` on `GET /v1/admin/data-planes/:id/compute`.
*
* # ⛔⛔ WHY THIS EXISTS AT ALL — AND WHY IT IS NOT SOURCED FROM CROSSPLANE
*
* `PoolComputeView.liveNodeCount` shipped hardcoded `null` (*"null until WS-5 wired"*) and WS-5 was
* never wired, so the Capacity screen has read **"— unknown"** fleet-wide, for everyone, since it
* shipped. The obvious CP-side fix is the `NodePool` managed resource the CP already owns:
* `status.atProvider.nodeCount` is populated, plausible, and `Synced=True`.
*
* ⛔ **It is wrong, and it was measured wrong on production on 2026-08-07** (`dp-app-cluster`):
*
* | pool | `atProvider.nodeCount` | REAL nodes |
* |---|---|---|
* | `infra-e2-standard-4` | 1 | **3** |
* | `default-app-e2-standard-4` | 1 | **4** |
* | `dp-app-node-pool` (legacy) | 1 | **3** |
* | `e2-standard-4` (demand) | **0** | **2** |
* | **total** | **3** | **12** |
*
* It echoes `initialNodeCount` — the desired per-zone seed — and never the autoscaled truth. It would
* render **"0 nodes"** for a demand pool running two and understate the fleet by 75%.
*
* ⚠️ **And it is NOT staleness**, which is what makes it lethal: all five pools were `Synced=True`, so
* every freshness check (conditions, `lastTransitionTime`, `observedGeneration`) **passes**. Nothing in
* the resource's own status would ever tell you the field answers a different question.
* ⛔ **LEAD RULING (2026-08-07): Crossplane `status.atProvider` is BANNED as a capacity source.**
*
* The second tempting shortcut is also closed, deliberately: there is no CP-side `listNode()` against a
* GKE DP, because `getClusterCredentials` returns `{ kubeconfig: '', scheme: 'rest-native' }` for `gcp`
* (§7.12 Phase B removed the CP's kubeconfig on purpose). ⛔ Re-opening a closed trust boundary to draw
* a display number is not a trade this platform makes.
*
* ⇒ The only truthful producer is the DP operator, which already holds cluster-wide
* `nodes: get,list,patch` and already calls `listNode()` each cycle. Reuse, not invention.
*/
/**
* Which pool a node belongs to, derived from the labels the composition already stamps.
*
* ⛔ **`unattributed` is a first-class member, not an error bucket.** On the live fleet **3 of 12**
* real nodes sit in the legacy conflated `{xr}-node-pool`, which carries **no Zion label at all** and
* which `DpComputeView` does not model. A census that silently dropped them would report a
* *complete-looking* 9 — the same defect class as the Crossplane echo it replaces, arriving through
* arithmetic instead of a field. Every node lands in exactly one role, and the roles sum to the
* cluster.
*/
const LIVE_NODE_POOL_ROLES = [
	"infra",
	"default-app",
	"demand",
	"dedicated",
	"isolated",
	"unattributed",
	"app-pool"
];
/**
* One row of the census: every node sharing a (role, nodeType) pair in one cluster. A home row
* carries no `region`; a row merged from a satellite carries its region (R-005). An `app-pool` row
* may also carry the `groupId` of its node group, and then the row holds the nodes of that one
* group (2026-10-24-pool-machine-generations D-35): two groups of one machine type, such as a spot
* tier and an on-demand tier, are two rows.
*
* ⛔ **There is no `null`/"unknown" arm on these counts, by construction.** A count that could not be
* taken is expressed by the whole `livePools` field being **absent** from the status report — never by
* a zero and never by a null inside it. See {@link liveNodePoolSchema}'s `nodeCount` note.
*/
const liveNodePoolSchema = z.object({
	role: z.enum(LIVE_NODE_POOL_ROLES),
	/**
	* The machine type of the nodes in this row, from the standard `node.kubernetes.io/instance-type`
	* label — i.e. **what the machines actually are**, not what the pool was requested to be.
	*
	* ⚠️ `null` when the node carries no instance-type label (bare/edge clusters). It is a *missing
	* label*, not an empty string: an empty string would collide with a real-but-unnamed type in the
	* group-by key.
	*/
	nodeType: z.string().min(1).nullable(),
	/**
	* Nodes in this row. ⛔ A genuine, measured `0` is legal and meaningful — an enabled demand pool
	* with nothing scheduled on it really is running zero nodes, and that is an ANSWER. "We could not
	* look" is the absence of the whole field, one level up.
	*/
	nodeCount: z.number().int().nonnegative(),
	/**
	* The subset whose `Ready=True`. ⛔ Always `<= nodeCount`; a booting or `NotReady` node is counted
	* in `nodeCount` and excluded here.
	*
	* ⚠️ **REQUIRED AND NON-NULLABLE HERE — the "could not count" state does NOT exist at this
	* layer.** A DP that reports a pool row reports both counts, and the row refine (`readyNodeCount <=
	* nodeCount`) is what makes the pair meaningful: `nodeCount: 5, readyNodeCount: 0` is a real,
	* fully-observed pool with nothing Ready — a reroll, or a wedged pool — and it is emphatically
	* NOT the same fact as "the census could not be taken."
	*
	* ⛔ **THAT SECOND FACT IS REPRESENTABLE ONLY DOWNSTREAM, AND `?? 0` IS THE BUG IT INVITES.**
	* `joinLiveNodeCounts` carries it as `readyPerPool: (number | null)[]`, `null` for an
	* un-censused pool (`provisioning-svc/src/modules/provisioning/live-node-join.ts`), and the view
	* types it `.nullable().optional()`, where **an ABSENT KEY and `null` mean the same thing**
	* (`@zion/sdk-contracts` `dp-compute-view.ts` — read that docblock before consuming this: it is
	* explicit that the counts are NOT `null` in lockstep, so "both are null" is a false pairing to
	* rely on). Coalescing either to `0` renders "we could not count" as "zero nodes are Ready"
	* on a healthy pool, which is the one misreading this field exists to prevent.
	*
	* ⇒ Removing the field is the worse option and always was: the schema is `.strict()` with the
	*   `readyNodeCount <= nodeCount` refine, so every already-persisted
	*   `release.dp_live_status.live_pools` row becomes unparseable the moment anyone adds a
	*   read-side parse. Cheap to keep, expensive to re-derive.
	*
	* ⛔ **DO NOT re-add an adoption note here.** This docblock used to carry a measured inventory
	*   of consumers — *"No consumer reads it"*, naming the lines that summed `nodeCount` alone. Every
	*   clause was true on the day it was written and **all four were false within days**, because
	*   a producer-without-a-consumer note is self-invalidating the moment someone adds the consumer
	*   and nothing links the two files. ⇒ Document the SEMANTICS, which do not rot; leave adoption
	*   to the consumers' own docblocks and to the tests that pin them.
	*/
	readyNodeCount: z.number().int().nonnegative(),
	/**
	* R-15b — the SUM of `.status.allocatable.cpu` across the nodes in this row, in MILLICORES.
	*
	* ⛔⛔ ABSENT IS "NOT MEASURED" AND IS NEVER ZERO, and here the stakes are higher than on
	* `nodeCount`: this value is a DENOMINATOR. A zero denominator does not render as "unknown",
	* it renders as a division by zero or as 100 % utilisation on a healthy node.
	*
	* ⚠️ The producer omits it for the WHOLE ROW unless EVERY node in the row reported a parseable
	* allocatable (`node-census.ts`). A row is a (role, nodeType) group, so its members are the
	* same machine type and a partial sum would silently understate the denominator — i.e. OVERSTATE
	* utilisation, the direction that raises a false alarm.
	*
	* ⚠️ Millicores, not cores: `.status.allocatable.cpu` is a Kubernetes quantity that is routinely
	* `"3920m"`. An integer millicore total is exact; a float core total is not.
	*/
	allocatableCpuMilli: z.number().int().nonnegative().optional(),
	/**
	* R-15b — the SUM of `.status.allocatable.memory` across the nodes in this row, in BYTES.
	* Same absent-is-not-zero and same all-or-nothing-per-row rule as {@link allocatableCpuMilli}.
	*
	* ⚠️ Bytes, not MiB/GiB: the source quantity is `"16311412Ki"`-shaped and only a byte integer is
	* lossless. `z.number().int()` is safe here — a `2^53` byte total is 9 PiB per pool row.
	*/
	allocatableMemoryBytes: z.number().int().nonnegative().optional(),
	/**
	* The MAXIMUM, over the nodes in this row, of
	* `allocatable.cpu - sum(requests.cpu of EVERY pod on that node)`, in MILLICORES.
	*
	* ⛔⛔ THIS IS A MAXIMUM OVER MEMBERS, NOT A SUM. The name says `OnAnyNode` for that reason and
	* must keep saying it. A row is a (role, nodeType) group and every other quantity on it is a
	* sum, so a reader who pattern-matches the neighbours will add this one across rows and get a
	* number that means nothing.
	*
	* ⛔ WHY A MAXIMUM IS THE ONLY USEFUL FORM. A pod is scheduled onto ONE node, so the question
	* "can this pod land" is answered by the largest single gap, never by the total. MEASURED on
	* `dp-app` 2026-08-31: `default-app-e2-standard-4` had 2834 millicores free in aggregate and
	* 856 on its largest node, against a pod requesting 2075. The pool-level sums said there was
	* room; nothing fit; the rollout deadlocked for 4.4 hours. Every existing field on this row
	* would have reported that pool as healthy.
	*
	* ⛔ FOUR WAYS TO COMPUTE THIS WRONG, ALL MEASURED, ALL OVERSTATING FREE SPACE:
	*   1. `listNamespacedPod` sees 74.9 % of committed CPU on that pool (`dp-app` 24910m,
	*      `kube-system` 5290m, `istio-system` 3000m) and would have reported 14.3 CPU free
	*      DURING the outage. The producer MUST use `listPodForAllNamespaces`.
	*   2. `PodSnapshot` requires `appScope`, so it excludes `kube-system`, `istio-system` and
	*      every DaemonSet. Do NOT derive the numerator from it.
	*   3. **Omitting native sidecars.** +20%. A native sidecar has `restartPolicy: Always` inside
	*      `initContainers`, so a probe of `spec.containers` alone misses every one. MEASURED:
	*      **149 of 766 containers are native sidecars and ALL 149 declare a CPU request.** Not a
	*      corner case.
	*   4. A partial commitment sum. See the all-or-nothing rule below, whose sign is FLIPPED
	*      relative to the allocatable denominators above.
	*
	* ⭐ ALL FOUR QUANTIFIED on `default-app-e2-standard-4`, 10 nodes, one instant (C-031). Every
	* one overstates free space:
	*
	*   | numerator                         | committed | maxFreeCpuMilliOnAnyNode |
	*   |-----------------------------------|-----------|--------------------------|
	*   | cluster-wide + sidecars (CORRECT) |   33250m  | **951**                  |
	*   | omit native sidecars              |   31380m  | 1137  (+20%)             |
	*   | listNamespacedPod('dp-app')       |   24910m  | 1785  (+88%)             |
	*   | PodSnapshot (appScope)            |   23910m  | 1885  (**+98%**)         |
	*
	* ⚠️ ABSENT IS "NOT MEASURED" AND IS NEVER ZERO. A genuine measured `0` is legal and means the
	* fullest node in this row has no room, which is an ANSWER. The producer omits BOTH scalars for
	* the WHOLE ROW unless every node in the row yielded a parseable allocatable AND a complete pod
	* list.
	*
	* ⛔ THE ALL-OR-NOTHING RULE MATTERS HERE WITH THE OPPOSITE SIGN TO `allocatableCpuMilli`. A
	* partial ALLOCATABLE sum understates the denominator and thus OVERSTATES utilisation, which is
	* alarming and safe. A partial COMMITMENT sum understates commitment and thus OVERSTATES FREE
	* SPACE, which is reassuring and unsafe. Emit both or neither.
	*
	* ⚠️ NOT A "WILL IT FIT" PREDICATE. This ignores taints, tolerations and node affinity, and
	* every `default-app` node carries `zion.witness.ai/pool:NoSchedule`. So for a pod without that
	* toleration this OVERSTATES availability. A consumer that renders it as "space available" is
	* wrong; a consumer that renders it as "largest gap on any node in this pool" is right.
	*/
	maxFreeCpuMilliOnAnyNode: z.number().int().nonnegative().optional(),
	/**
	* The same maximum for memory, in BYTES: `allocatable.memory - sum(requests.memory of EVERY pod
	* on that node)`, maximised over the nodes in this row.
	*
	* ⚠️ Memory is frequently the binding constraint before CPU is, so this is not a decoration on
	* the CPU scalar. It is emitted under the same all-or-nothing rule and carries every warning
	* above unchanged.
	*/
	maxFreeMemoryBytesOnAnyNode: z.number().int().nonnegative().optional(),
	/**
	* The satellite region whose cluster these nodes run in (2026-09-29-gpu-and-regional-app-pools
	* R-005), in `APP_POOL_REGION_PATTERN`'s grammar. Absent means the data plane's home cluster,
	* which is every row a home census produces and every row persisted before this field existed.
	*
	* The home operator merges each satellite's `app-pool` census rows into
	* `StatusReport.livePools` with this member set, and release-engine persists the array
	* verbatim into `release.dp_live_status.live_pools`. So one machine type can appear in two rows
	* of one role, one per cluster, and a consumer that sums by `nodeType` alone collides two
	* regions' counts: `appPoolLiveNodeCounts` keys by `(nodeType, region)` (WS-13).
	*
	* Optional, so an older operator's report parses unchanged; the object stays `.strict()`, so a
	* newer operator's report fails an older CP (REL-1, CP first).
	*/
	region: appPoolRegionSchema.optional(),
	/**
	* The node group whose nodes this row counts (2026-10-24-pool-machine-generations D-35): the
	* value of the node label `zion.witness.ai/app-pool-group`, an `apg-` id in
	* `APP_POOL_NODE_GROUP_ID_PATTERN`'s grammar. Present only on a row with `role: 'app-pool'` (a
	* refine below).
	*
	* Why: a row keyed by `(role, nodeType)` alone holds every group of one machine type. So a spot
	* group and an on-demand group of `c4d-standard-8` share one row, and metering cannot price
	* either group.
	*
	* The producer obligation: the operator census must key an `app-pool` row by this id and set
	* this member (WS-3c, stage S3c). Then a reader keyed on `(role, nodeType)` can get two rows for
	* one machine type, and it must accept them (DEF-10). An operator before WS-3c keys every row by
	* `(role, nodeType)` and sends no `groupId`. Its `app-pool` row holds every group of the machine
	* type, and no reader can split that row by group.
	*
	* The consumer obligation: metering must attribute a row with a `groupId` to the group of that
	* id, and not to every group of the row's `(region, nodeType)` (WS-3g, stage S3c). Before WS-3g,
	* `appPoolLiveNodeCounts` in platform-api reads no `groupId`. It counts nothing for two groups
	* of one machine type in one region, so neither group gets a usage record.
	*
	* A satellite census row (`satelliteCensusRowSchema` of `status-report.schemas.ts`) takes the
	* same member, because the satellite runs the same census code (WS-3c). Two groups of one
	* machine type in one region need no tiers: the regional pools of two apps are an example. So
	* `distinctCensusRows` keys a satellite census by `(role, nodeType, groupId, capacityType)`, and
	* two such rows parse. `appPoolCensusWithinEachGroup` holds the rows of one `groupId` to the
	* node count of that group's observation in the same message, so a satellite cannot bill one
	* group's nodes to another group.
	*
	* Absent on every row before this member existed, on every non-`app-pool` row, and on an
	* `app-pool` row whose nodes carry no group label. Optional, so an older operator's report
	* parses unchanged; the object stays `.strict()`, so a newer operator's report fails an older
	* CP (REL-1, CP first).
	*/
	groupId: z.string().regex(APP_POOL_NODE_GROUP_ID_PATTERN).optional(),
	/**
	* The capacity of the row's nodes (2026-10-24-pool-machine-generations D-35, D-36). The literal
	* `spot` marks nodes with the label `zion.witness.ai/app-pool-capacity: spot`
	* (`APP_POOL_CAPACITY_LABEL` of `app-pool-node-group.ts`). The member is absent for on-demand
	* nodes. `on-demand` and `null` are parse errors (DEF-1).
	*
	* Present only on an `app-pool` row that carries a `groupId` (a refine below). A spot node
	* belongs to one spot group, and the census row of that group names it.
	*
	* The consumer obligation: metering must read this member to select the rate of the row (WS-3g,
	* stage S3c). A row with `capacityType: 'spot'` takes the spot rate of its machine type. A row
	* with no member takes the on-demand rate. A reader that ignores this member prices a spot row at
	* the on-demand rate. Before WS-3g, `meterNodeGroups` in platform-api looks up a rate by the
	* machine type alone.
	*/
	capacityType: appPoolSpotCapacitySchema.optional()
}).strict().refine((p) => p.readyNodeCount <= p.nodeCount, {
	message: "readyNodeCount cannot exceed nodeCount",
	path: ["readyNodeCount"]
}).refine((p) => p.groupId === void 0 || p.role === "app-pool", {
	message: "groupId rides only an app-pool row",
	path: ["groupId"]
}).refine((p) => p.capacityType === void 0 || p.role === "app-pool" && p.groupId !== void 0, {
	message: "capacityType rides only an app-pool row that carries a groupId",
	path: ["capacityType"]
});
//#endregion
export { LIVE_NODE_POOL_ROLES, liveNodePoolSchema };

//# sourceMappingURL=live-node-pool.js.map